/**
 * Combat events → effects (plan G8). Reads the sim's event ring with its own cursor and runs the matching
 * recipe of the weapon or ship involved (style guide §3.6: impact and muzzle colours belong to the weapon):
 * - `fire` / `torpedoLaunch` → the weapon's muzzle effect at the muzzle, along the firing direction.
 * - `hitShield` / `hitHull` → the weapon's shield or hull impact at the hit point, along the surface normal.
 *   Torpedo hits are skipped: the torpedo's `explode` event at the same point draws its blast.
 * - `explode` → a torpedo's own impact effect (eid -1), or the ship's explosion (`fx.json` explosions:
 *   `explosionLarge` for the gunship, `explosionSmall` for fighters). `kill` draws nothing because the sim
 *   always follows it with `explode` (sim.ts step 6), so a death never explodes twice.
 *
 * Effects inherit the velocity of the ship involved (shooter, victim or the exploding ship). Event times:
 * events of step 3 (fire, hits, launches) happen at the start of their tick, `explode` (step 6) at its end.
 * Allocation-free (D-014).
 */
import { EV, EVENT_MASK, firstReadable, type EventRing } from '../../combat/events'
import type { ShipDef, WeaponsData } from '../../data/schemas'
import type { VfxEffectId } from '../../render/contracts'
import { MAX_ENTITIES, Ship, Vel } from '../../world/components'
import type { FxConfig } from './config'
import { emitEffect, type ParticleSystem } from './particles'

export type ExplosionConfig = FxConfig['explosions']

export interface CombatFxReader {
  /** First event sequence number not yet read. */
  cursor: number
  /** Effects emitted so far (tests and the perf overlay). */
  effects: number
}

/** A reader that starts at the ring's current head (older events are not replayed). */
export function createCombatFxReader(ring: EventRing): CombatFxReader {
  return { cursor: ring.head, effects: 0 }
}

/**
 * The def of the ship that exploded: `Ship.defIndex[eid]` when its collider radius matches the event's size
 * hint (the entity may already be despawned and its id reused), else the first def with that radius.
 */
export function explodedDef(eid: number, amount: number, defs: readonly ShipDef[]): ShipDef | null {
  if (eid < 0 || eid >= MAX_ENTITIES) return null
  const own = defs[Ship.defIndex[eid]]
  if (own && Math.fround(own.colliderRadius) === amount) return own
  for (let i = 0; i < defs.length; i++) if (Math.fround(defs[i].colliderRadius) === amount) return defs[i]
  return own ?? null
}

/** The effect for event slot `s`, or null when the event draws nothing. */
export function effectForEvent(ring: EventRing, s: number, weapons: WeaponsData, defs: readonly ShipDef[], explosions: ExplosionConfig): VfxEffectId | null {
  const type = ring.type[s]
  const w = weapons.weapons[ring.weapon[s]]
  switch (type) {
    case EV.fire:
    case EV.torpedoLaunch:
      return w ? w.vfx.muzzle : null
    case EV.hitShield:
      return w && w.kind !== 'torpedo' ? w.vfx.shieldImpact : null
    case EV.hitHull:
      return w && w.kind !== 'torpedo' ? w.vfx.impact : null
    case EV.explode: {
      const eid = ring.eid[s]
      if (eid < 0) return w ? w.vfx.impact : explosions.defaultShip
      const def = explodedDef(eid, ring.amount[s], defs)
      return (def && explosions.ships[def.id]) || explosions.defaultShip
    }
    default:
      return null
  }
}

/** Seconds after its tick's start at which an event happened (0 for step 3, one tick for step 6). */
export function eventPhase(type: number): 0 | 1 {
  return type === EV.explode ? 1 : 0
}

/**
 * Emits the effect of every unread event. `tickStart` and `tickEnd` are the sim times of the tick the
 * events belong to (in a frame catch-up, both are the render time).
 */
export function readCombatEvents(
  reader: CombatFxReader,
  sys: ParticleSystem,
  ring: EventRing,
  weapons: WeaponsData,
  defs: readonly ShipDef[],
  explosions: ExplosionConfig,
  tickStart: number,
  tickEnd: number,
): void {
  const head = ring.head
  for (let seq = firstReadable(ring, reader.cursor); seq < head; seq++) {
    const s = seq & EVENT_MASK
    const effect = effectForEvent(ring, s, weapons, defs, explosions)
    if (effect === null) continue
    const eid = ring.eid[s]
    const hasShip = eid >= 0 && eid < MAX_ENTITIES
    const time = eventPhase(ring.type[s]) === 1 ? tickEnd : tickStart
    emitEffect(
      sys,
      effect,
      ring.x[s],
      ring.y[s],
      ring.z[s],
      ring.dx[s],
      ring.dy[s],
      ring.dz[s],
      hasShip ? Vel.x[eid] : 0,
      hasShip ? Vel.y[eid] : 0,
      hasShip ? Vel.z[eid] : 0,
      time,
    )
    reader.effects++
  }
  reader.cursor = head
}
